import {
  Card,
  Text,
  Stack,
  LoadingOverlay,
  Container,
  Group,
  Badge,
  Title,
  Space,
  Divider,
  Accordion,
  ScrollArea
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import ModalPopupComponent from 'components/ModalPopup';
import ModalPopupConfirmationComponent from 'components/ModalPopupConfirmationComponent';
import SecondaryButton from 'components/SecondaryButton';
import React, { useState, useEffect } from 'react';
import { masterGroupService } from 'services/lia/masterGroupService';
import { MasterGroup } from 'services/api';
import PageTitle from 'components/PageTitle';
import { IconEdit } from '@tabler/icons-react';
import Toolbar from 'components/Toolbar';
import DataTables, { DataTableColumn } from 'components/DataTables';
import dayjs from 'dayjs';

interface RangeItem {
  msQuestionRangeItemID: string;
  msQuestionID: string;
  label: string;
  value: string;
  sequence: number;
  userIn: string | null;
  userUp: string | null;
  dateIn: string | null;
  dateUp: string | null;
  isDeleted: boolean;
}

interface Question {
  msQuestionID: string;
  title: string;
  description: string;
  type: string;
  msGroupQuestionID: string;
  groupName: string | null;
  sequence: number;
  isDeleted: boolean;
  dateIn: string;
  dateUp: string | null;
  userIn: string;
  userUp: string | null;
  rangeItem?: RangeItem[];
}

interface ExtendedMasterGroup extends MasterGroup {
  questions?: Question[];
}

const MasterGroupPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortBy, setSortBy] = useState('');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // Modal states
  const [opened, { open, close }] = useDisclosure();
  const [detailOpened, { open: openDetail, close: closeDetail }] = useDisclosure();
  const [selectedGroup, setSelectedGroup] = useState<ExtendedMasterGroup | null>(null);
  const [groupToDelete, setGroupToDelete] = useState<string | null>(null);

  const searchParams = new URLSearchParams(location.search);
  const defaultSearch = searchParams.get('search') || '';

  // Keep track of previous search to detect changes
  const [previousSearch, setPreviousSearch] = useState(defaultSearch);

  // Reset to page 1 when search changes
  useEffect(() => {
    if (defaultSearch !== previousSearch) {
      setCurrentPage(1);
      setPreviousSearch(defaultSearch);
    }
  }, [defaultSearch, previousSearch]);

  // Fetch master groups
  const { data: groupsData, isLoading } = useQuery({
    queryKey: ['masterGroups', defaultSearch, currentPage, pageSize, sortBy, sortOrder],
    queryFn: () =>
      masterGroupService.getList({
        search: defaultSearch,
        page: currentPage,
        limit: pageSize,
        sortBy,
        sortOrder
      })
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => masterGroupService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['masterGroups'] });
      close();
    }
  });

  const [columns, setColumns] = useState<DataTableColumn[]>([
    {
      heading: 'Name',
      props: 'title',
      sortable: true,
      active: true,
      id: 'title'
    },
    {
      heading: 'Description',
      props: ({ desc }: MasterGroup) => (desc.length > 50 ? `${desc.slice(0, 150)}...` : desc),
      sortable: true,
      active: true,
      id: 'desc'
    },
    {
      heading: 'Created By',
      props: 'userIn',
      sortable: true,
      active: true,
      id: 'userIn'
    },
    {
      heading: 'Created Date',
      props: ({ dateIn }: MasterGroup) => dayjs(dateIn).format('DD MMM YYYY HH:mm'),
      sortable: true,
      active: true,
      id: 'dateIn'
    },
    {
      heading: 'Updated By',
      props: ({ userUp }: MasterGroup) => userUp || '-',
      sortable: true,
      active: true,
      id: 'userUp'
    },
    {
      heading: 'Updated Date',
      props: ({ dateUp }: MasterGroup) =>
        dateUp ? dayjs(dateUp).format('DD MMM YYYY HH:mm') : '-',
      sortable: true,
      active: true,
      id: 'dateUp'
    },
    {
      heading: 'Action',
      props: 'action',
      active: true,
      id: 'action'
    }
  ]);

  // Pagination handlers
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const handleView = (row: ExtendedMasterGroup) => {
    setSelectedGroup(row);
    openDetail();
  };

  const handleEdit = (row: MasterGroup) => {
    // Navigate in the same tab
    const url = `/app/lia/questions/group-questions/edit?id=${row.msGroupQuestionID}`;
    navigate(url);
  };

  const handleDelete = (row: MasterGroup) => {
    setGroupToDelete(row.msGroupQuestionID);
    open();
  };

  const handleCreateClick = () => {
    // Navigate in the same tab
    navigate('/app/lia/questions/group-questions/add');
  };

  const processDelete = async () => {
    if (groupToDelete) {
      await deleteMutation.mutateAsync(groupToDelete);
    }
  };

  const tableData = {
    data: groupsData?.data || [],
    pageNumber: groupsData?.pageNumber || 1,
    pageSize: groupsData?.pageSize || 10,
    totalPages: groupsData?.totalPages || 0,
    totalRecords: groupsData?.totalRecords || 0
  };

  return (
    <Container fluid>
      <ModalPopupConfirmationComponent
        buttonText='Delete'
        statusProcess='delete'
        opened={opened}
        open={open}
        close={close}
        onSubmit={processDelete}
      />

      <ModalPopupComponent
        opened={detailOpened}
        close={closeDetail}
        size='xl'
        withCloseButton={true}
      >
        <Stack gap={0}>
          {/* Header */}
          <Group justify='space-between' align='flex-start' p='md' pb='lg'>
            <Stack gap={4}>
              <Title order={2} c='dark'>
                Group Details
              </Title>
              <Text size='sm' c='dimmed'>
                View and manage group question information
              </Text>
            </Stack>
            {selectedGroup && (
              <Group gap='sm'>
                <Badge
                  color={selectedGroup.isDeleted ? 'red' : 'green'}
                  variant='light'
                  size='lg'
                  radius='md'
                >
                  {selectedGroup.isDeleted ? 'Inactive' : 'Active'}
                </Badge>
                <SecondaryButton
                  text='Edit Group'
                  leftSection={<IconEdit size={16} />}
                  onClick={() => handleEdit(selectedGroup)}
                />
              </Group>
            )}
          </Group>

          <Divider />

          {/* Content */}
          {selectedGroup && (
            <ScrollArea style={{ height: '60vh' }} p='xl'>
              <Stack gap='xl'>
                {/* Basic Information */}
                <Card withBorder p='lg' radius='lg'>
                  <Stack gap='md'>
                    <Text fw={600} size='lg' c='dark'>
                      📋 Basic Information
                    </Text>

                    <Stack gap='sm'>
                      <Text size='sm' fw={500} c='dimmed'>
                        Group Name
                      </Text>
                      <Text fw={500} size='md'>
                        {selectedGroup.title}
                      </Text>
                    </Stack>

                    <Stack gap='sm'>
                      <Text size='sm' fw={500} c='dimmed'>
                        Description
                      </Text>
                      <Text size='sm' style={{ lineHeight: 1.6 }}>
                        {selectedGroup.desc}
                      </Text>
                    </Stack>

                    <Group>
                      <Stack gap='sm' flex={1}>
                        <Text size='sm' fw={500} c='dimmed'>
                          Module ID
                        </Text>
                        <Badge variant='outline' size='md' color='blue'>
                          {selectedGroup.moduleID}
                        </Badge>
                      </Stack>
                    </Group>
                  </Stack>
                </Card>

                {/* Audit Information */}
                <Card withBorder p='lg' radius='lg'>
                  <Stack gap='md'>
                    <Text fw={600} size='lg' c='dark'>
                      📅 Audit Information
                    </Text>

                    <Group grow>
                      <Stack gap='sm'>
                        <Text size='sm' fw={500} c='dimmed'>
                          Created By
                        </Text>
                        <Group gap='xs'>
                          <Badge variant='light' color='blue'>
                            {selectedGroup.userIn}
                          </Badge>
                        </Group>
                      </Stack>

                      <Stack gap='sm'>
                        <Text size='sm' fw={500} c='dimmed'>
                          Created Date
                        </Text>
                        <Text size='sm'>
                          {dayjs(selectedGroup.dateIn).format('DD MMM YYYY, HH:mm')}
                        </Text>
                      </Stack>
                    </Group>

                    <Group grow>
                      <Stack gap='sm'>
                        <Text size='sm' fw={500} c='dimmed'>
                          Updated By
                        </Text>
                        <Group gap='xs'>
                          <Badge variant='light' color='gray'>
                            {selectedGroup.userUp || 'N/A'}
                          </Badge>
                        </Group>
                      </Stack>

                      <Stack gap='sm'>
                        <Text size='sm' fw={500} c='dimmed'>
                          Updated Date
                        </Text>
                        <Text size='sm'>
                          {selectedGroup.dateUp
                            ? dayjs(selectedGroup.dateUp).format('DD MMM YYYY, HH:mm')
                            : 'Never updated'}
                        </Text>
                      </Stack>
                    </Group>
                  </Stack>
                </Card>

                {/* Questions Section */}
                <Card withBorder p='lg' radius='lg'>
                  <Stack gap='lg'>
                    <Group justify='space-between' align='center'>
                      <Text fw={600} size='lg' c='dark'>
                        🔍 Questions in this Group
                      </Text>
                      <Badge size='lg' variant='light' color='blue'>
                        {selectedGroup.questions?.length || 0} questions
                      </Badge>
                    </Group>

                    {selectedGroup.questions && selectedGroup.questions.length > 0 ? (
                      <Stack gap='md'>
                        {selectedGroup.questions.map((question, index) => (
                          <Card
                            key={question.msQuestionID}
                            withBorder
                            p='md'
                            radius='md'
                            style={{ backgroundColor: '#fafafa' }}
                          >
                            <Stack gap='sm'>
                              <Group justify='space-between' align='flex-start'>
                                <Group gap='sm'>
                                  <Badge color='blue' variant='filled' size='sm'>
                                    Q{index + 1}
                                  </Badge>
                                  <Badge color='indigo' variant='light' size='sm'>
                                    {question.type}
                                  </Badge>
                                </Group>
                              </Group>

                              <Text fw={500} size='sm' lineClamp={2}>
                                {question.title}
                              </Text>

                              {question.description && (
                                <Text size='xs' c='dimmed' lineClamp={2}>
                                  {question.description}
                                </Text>
                              )}

                              {question.type === 'Range' &&
                                question.rangeItem &&
                                question.rangeItem.length > 0 && (
                                  <Accordion variant='contained' radius='sm'>
                                    <Accordion.Item value='range-options'>
                                      <Accordion.Control>
                                        <Text size='xs' fw={500}>
                                          View Range Options ({question.rangeItem.length})
                                        </Text>
                                      </Accordion.Control>
                                      <Accordion.Panel>
                                        <Stack gap='xs'>
                                          {question.rangeItem.map((item) => (
                                            <Group
                                              key={item.msQuestionRangeItemID}
                                              justify='space-between'
                                            >
                                              <Text size='xs'>
                                                {item.sequence}. {item.label}
                                              </Text>
                                              <Badge size='xs' color='gray'>
                                                {item.value}
                                              </Badge>
                                            </Group>
                                          ))}
                                        </Stack>
                                      </Accordion.Panel>
                                    </Accordion.Item>
                                  </Accordion>
                                )}
                            </Stack>
                          </Card>
                        ))}
                      </Stack>
                    ) : (
                      <Stack align='center' py='xl' gap='md'>
                        <Text size='lg' c='dimmed'>
                          📝
                        </Text>
                        <Text size='sm' c='dimmed' ta='center'>
                          No questions in this group yet
                        </Text>
                      </Stack>
                    )}
                  </Stack>
                </Card>
              </Stack>
            </ScrollArea>
          )}
        </Stack>
      </ModalPopupComponent>

      <PageTitle
        title='Master Group Question'
        description='Create and manage group questions. Easily add, edit, or remove group question to build comprehensive grouping for your questions.'
      />

      <Card shadow='xs' radius='md' mt={20} pos='relative'>
        <LoadingOverlay
          visible={isLoading || deleteMutation.isPending}
          overlayProps={{ radius: 'sm', blur: 2 }}
        />
        <Toolbar
          columns={columns}
          createButtonText='Create Group'
          onCreate={handleCreateClick}
          defaultSearchValue={defaultSearch}
          setColumns={setColumns}
        />
        <Space h={20} />
        <DataTables
          columns={columns}
          data={tableData.data}
          loading={isLoading}
          activeSize={pageSize}
          sizing={[
            { label: '10', value: 10 },
            { label: '20', value: 20 },
            { label: '50', value: 50 }
          ]}
          withPagination
          totalData={tableData.totalRecords}
          currentPage={currentPage}
          currentSize={pageSize}
          handleChangePage={handlePageChange}
          handleChangeSize={handleSizeChange}
          withBulkData={false}
          handleView={handleView}
          handleEdit={handleEdit}
          handleDelete={handleDelete}
          viewText='View Group'
          editText='Edit Group'
          deleteText='Delete Group'
          emptyMessageTitle='No Group Questions'
          onSortChange={(newSortBy, newSortOrder) => {
            setSortBy(newSortBy);
            setSortOrder(newSortOrder);
            setCurrentPage(1);
          }}
          emptyMessageDescription='Click create button to add new group question'
        />
      </Card>
    </Container>
  );
};

export default MasterGroupPage;
